// Utility for name translations / overrides
// - Add mappings to `lib/nameMappings.json` for names that Google Translate may mistranslate into Gujarati
// - This module exports helper functions used on the client to detect Gujarati translation and return a localized name

// The actual mappings are stored in a JSON file so you can add or edit entries without changing the JS code.
import nameMappingsJson from './nameMappings.json';

// Create a mutable copy so client-side code can reference it
export const nameMappings = { ...nameMappingsJson };

export function normalizeKey({ name = '', surname = '', fatherName = '' } = {}) {
    return `${surname} ${name} ${fatherName}`.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function assembleName({ name = '', surname = '', fatherName = '' } = {}) {
    // Default display used server-side or when no mapping applies
    const parts = [];
    if (surname) parts.push(String(surname).toLowerCase());
    if (name) parts.push(String(name).toLowerCase());
    if (fatherName) parts.push(String(fatherName).toLowerCase());
    return parts.join(' ').trim();
}

export function isGujaratiTranslationDetected() {
    if (typeof window === 'undefined') return false;

    try {
        // 1) html lang attribute
        const lang = document.documentElement?.getAttribute('lang') || document.documentElement?.lang || '';
        if (lang && lang.toLowerCase().startsWith('gu')) return true;

        // 2) navigator language
        if (navigator?.language?.toLowerCase().startsWith('gu')) return true;

        // 3) googtrans cookie is commonly used by Google Translate: value like '/en/gu'
        const cookies = document.cookie || '';
        if (/googtrans=[^;]*\/gu/.test(cookies)) return true;

        // 4) detect Google Translate UI elements (extension/website translator)
        if (document.querySelector('.goog-te-banner-frame, iframe.goog-te-menu-frame, .translated-ltr')) return true;
    } catch (err) {
        // swallow any errors during detection
        console.warn('Gujarati translation detection failed', err);
    }

    return false;
}

export function getLocalizedName(user) {
    if (!user) return '';

    const key = normalizeKey(user);
    if (isGujaratiTranslationDetected() && nameMappings[key]) {
        return nameMappings[key];
    }

    return assembleName(user);
}

// Server-side helper script exists in `scripts/update-name-mapping.mjs` to add mappings to the JSON file.
// Client-side `addNameMapping` will only mutate in-memory map and won't persist to disk.
export function addNameMapping(latinKey, gujaratiValue) {
    if (!latinKey || !gujaratiValue) return;
    const key = latinKey.trim().toLowerCase();
    nameMappings[key] = gujaratiValue;
}
